import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Gift, Copy, Share2, Check, Sparkles, Crown, Zap,
  BadgeCheck, UserPlus, MessageSquare, Shield, AlertTriangle,
} from 'lucide-react';

/**
 * Referrals page — "Bring a Friend" (Phase R1).
 *
 * Two-sided reward: 7 days Premium + 3 Boosts + 5 Serious Interests.
 * Rewards unlock only after the invited friend clears 4 gates:
 *   1. Sign up   2. ID verified   3. Complete profile   4. Become active
 */

const GATE_STEPS = [
  { key: 'signed_up',        label: 'Signed up',        icon: UserPlus },
  { key: 'id_verified',      label: 'ID verified',      icon: BadgeCheck },
  { key: 'profile_complete', label: 'Profile complete', icon: Sparkles },
  { key: 'active',           label: 'Active',           icon: MessageSquare },
];

function StepPill({ step, done, active }) {
  const Icon = step.icon;
  return (
    <div className="flex flex-col items-center gap-1 flex-1 min-w-0" data-testid={`gate-${step.key}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all
        ${done ? 'bg-[#0F0F10] text-white' :
          active ? 'bg-white border-2 border-[#0F0F10] text-[#0F0F10]' :
                   'bg-[#F1F1F3] text-[#8A8A93]'}`}>
        {done ? <Check size={13} strokeWidth={3} /> : <Icon size={13} strokeWidth={2} />}
      </div>
      <span className={`text-[10px] font-semibold tracking-tight leading-tight text-center
        ${done ? 'text-[#0F0F10]' : 'text-[#8A8A93]'}`}>{step.label}</span>
    </div>
  );
}

function InviteRow({ invite }) {
  const stepsDone = invite.steps_done || 0;
  return (
    <div data-testid={`invite-row-${invite.referral_id}`}
      className="bg-white rounded-2xl border border-black/[0.06] p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      <div className="flex items-center gap-3 mb-3">
        {invite.photo_url ? (
          <img src={invite.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#F1F1F3] flex items-center justify-center flex-shrink-0">
            <UserPlus size={16} className="text-[#8A8A93]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-[#0F0F10] tracking-tight truncate">{invite.display_name}</p>
          <p className="text-[11.5px] text-[#8A8A93]">
            {invite.rewarded ? 'Both sides rewarded' :
             invite.flagged ? 'Flagged as duplicate account — no reward' :
             `Step ${stepsDone} of ${invite.steps_total}`}
          </p>
        </div>
        {invite.rewarded && (
          <span className="text-[10px] font-bold tracking-wide text-[#15803D] bg-[#DCFCE7] px-2.5 py-1 rounded-full uppercase">Rewarded</span>
        )}
        {invite.flagged && (
          <span className="text-[10px] font-bold tracking-wide text-[#B91C1C] bg-[#FEE2E2] px-2.5 py-1 rounded-full uppercase inline-flex items-center gap-1">
            <AlertTriangle size={10} /> Flagged
          </span>
        )}
      </div>
      <div className="flex items-start gap-1">
        {GATE_STEPS.map((step, i) => (
          <StepPill key={step.key} step={step} done={i < stepsDone} active={i === stepsDone} />
        ))}
      </div>
    </div>
  );
}

export default function Referrals() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [codeData, setCodeData] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get('/referral/my-code'),
          api.get('/referral/stats'),
        ]);
        setCodeData(c.data);
        setStats(s.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load referrals');
      }
      setLoading(false);
    })();
  }, []);

  const copyCode = async () => {
    if (!codeData?.code) return;
    try {
      await navigator.clipboard.writeText(codeData.code);
      setCopied(true);
      toast.success('Code copied');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyLink = async () => {
    if (!codeData?.share_url) return;
    try {
      await navigator.clipboard.writeText(codeData.share_url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const share = async () => {
    if (!codeData?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Meet to Keep',
          text: codeData.share_message,
          url: codeData.share_url,
        });
      } catch { /* user canceled */ }
    } else {
      copyLink();
    }
  };

  const rewards = stats?.rewards_earned || { premium_days: 0, boosts: 0, super_likes: 0 };
  const bundle = codeData?.reward || { premium_days: 7, boosts: 3, super_likes: 5 };

  const inProgress = useMemo(() =>
    (stats?.invites || []).filter(i => !i.rewarded && !i.flagged),
    [stats]);
  const completed = useMemo(() =>
    (stats?.invites || []).filter(i => i.rewarded),
    [stats]);
  const flagged = useMemo(() =>
    (stats?.invites || []).filter(i => i.flagged),
    [stats]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-24" data-testid="referrals-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3 bg-white border-b border-black/[0.04]">
        <button onClick={() => navigate('/settings')} data-testid="back-btn"
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#F1F1F3] transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">Bring a friend</p>
          <h1 className="text-[22px] font-bold tracking-tight text-[#0F0F10] leading-tight">Invite &amp; earn</h1>
        </div>
      </div>

      <div className="px-5 pt-5 space-y-5 max-w-md mx-auto">
        {/* Reward summary */}
        <section className="bg-gradient-to-br from-[#0F0F10] to-[#2A1A1A] rounded-2xl p-5 text-white shadow-lg"
                 data-testid="reward-summary">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={18} className="text-[#F87171]" />
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#F87171]">Both of you win</p>
          </div>
          <p className="text-[18px] font-bold tracking-tight leading-tight">
            {bundle.premium_days} days Premium + {bundle.boosts} Boosts + {bundle.super_likes} Serious Interests
          </p>
          <p className="text-[12.5px] text-white/70 mt-1.5 leading-snug">
            Unlocked when your friend verifies, completes their profile, and becomes active. Same reward for both sides.
          </p>
        </section>

        {/* My code + share */}
        <section className="bg-white rounded-2xl border border-black/[0.06] p-5 space-y-4"
                 data-testid="my-code-card">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-1.5">Your code</p>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 bg-[#F8F9FA] border border-black/[0.06] rounded-xl px-4 py-3.5 flex items-center">
                <span className="text-[18px] font-bold tracking-widest text-[#0F0F10] font-mono" data-testid="my-code-value">
                  {codeData?.code}
                </span>
              </div>
              <button onClick={copyCode} data-testid="copy-code-btn"
                className="w-12 rounded-xl border border-black/[0.06] bg-white hover:bg-[#F8F9FA] transition-all flex items-center justify-center">
                {copied ? <Check size={16} className="text-[#15803D]" /> : <Copy size={15} />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={share} data-testid="share-btn"
              className="bg-[#0F0F10] text-white py-3 rounded-xl text-[13px] font-semibold tracking-tight flex items-center justify-center gap-2 hover:opacity-90 transition-all active:scale-[0.98]">
              <Share2 size={14} /> Share invite
            </button>
            <button onClick={copyLink} data-testid="copy-link-btn"
              className="bg-white text-[#0F0F10] border border-black/[0.08] py-3 rounded-xl text-[13px] font-semibold tracking-tight flex items-center justify-center gap-2 hover:bg-[#F8F9FA] transition-all">
              <Copy size={14} /> Copy link
            </button>
          </div>
        </section>

        {/* Rewards earned */}
        <section className="bg-white rounded-2xl border border-black/[0.06] p-5" data-testid="rewards-earned-card">
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-3">Rewards earned</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center bg-[#F8F9FA] rounded-xl py-3">
              <Crown size={17} className="mx-auto text-[#B38138] mb-1" />
              <p className="text-[18px] font-bold text-[#0F0F10] tracking-tight leading-none" data-testid="stat-premium-days">{rewards.premium_days}</p>
              <p className="text-[10px] text-[#8A8A93] mt-1 leading-tight">Premium days</p>
            </div>
            <div className="text-center bg-[#F8F9FA] rounded-xl py-3">
              <Zap size={17} className="mx-auto text-[#B38138] mb-1" />
              <p className="text-[18px] font-bold text-[#0F0F10] tracking-tight leading-none" data-testid="stat-boosts">{rewards.boosts}</p>
              <p className="text-[10px] text-[#8A8A93] mt-1 leading-tight">Boosts</p>
            </div>
            <div className="text-center bg-[#F8F9FA] rounded-xl py-3">
              <Sparkles size={17} className="mx-auto text-[#B38138] mb-1" />
              <p className="text-[18px] font-bold text-[#0F0F10] tracking-tight leading-none" data-testid="stat-super-likes">{rewards.super_likes}</p>
              <p className="text-[10px] text-[#8A8A93] mt-1 leading-tight">Serious Interests</p>
            </div>
          </div>
        </section>

        {/* Invited-by (inbound) — only if this user was invited */}
        {stats?.invited_by && (
          <section className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3"
                   data-testid="invited-by-card">
            {stats.invited_by.referrer_photo ? (
              <img src={stats.invited_by.referrer_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#F1F1F3] flex items-center justify-center flex-shrink-0">
                <Gift size={14} className="text-[#8A8A93]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[#0F0F10] tracking-tight">
                {stats.invited_by.referrer_name} invited you
              </p>
              <p className="text-[11.5px] text-[#8A8A93] leading-snug mt-0.5">
                {stats.invited_by.rewarded
                  ? 'You&apos;ve unlocked your welcome bundle!'
                  : `You&apos;re on step ${stats.invited_by.steps_done} of ${stats.invited_by.steps_total} — keep going to unlock your reward.`}
              </p>
            </div>
          </section>
        )}

        {/* In-progress invites */}
        {inProgress.length > 0 && (
          <section data-testid="invites-in-progress">
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-2 px-1">
              In progress · {inProgress.length}
            </p>
            <div className="space-y-3">
              {inProgress.map(inv => <InviteRow key={inv.referral_id} invite={inv} />)}
            </div>
          </section>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <section data-testid="invites-completed">
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-2 px-1">
              Rewarded · {completed.length}
            </p>
            <div className="space-y-3">
              {completed.map(inv => <InviteRow key={inv.referral_id} invite={inv} />)}
            </div>
          </section>
        )}

        {/* Flagged (only if any) */}
        {flagged.length > 0 && (
          <section data-testid="invites-flagged">
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#B91C1C] mb-2 px-1">
              Flagged · {flagged.length}
            </p>
            <div className="space-y-3">
              {flagged.map(inv => <InviteRow key={inv.referral_id} invite={inv} />)}
            </div>
            <p className="text-[11px] text-[#8A8A93] mt-2 px-1 leading-snug">
              These accounts were flagged as duplicates of yours (same device or network). Rewards were not granted.
            </p>
          </section>
        )}

        {/* Empty state */}
        {(!stats || stats.invites.length === 0) && (
          <section className="bg-white rounded-2xl border border-black/[0.06] p-6 text-center" data-testid="empty-invites">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F1F1F3] flex items-center justify-center mb-3">
              <Gift size={22} className="text-[#8A8A93]" />
            </div>
            <p className="text-[15px] font-semibold text-[#0F0F10] tracking-tight">No invites yet</p>
            <p className="text-[12.5px] text-[#8A8A93] mt-1 leading-snug max-w-[240px] mx-auto">
              Share your code with friends who&apos;d actually take dating seriously. You&apos;ll both earn once they get active.
            </p>
          </section>
        )}

        {/* Trust footer */}
        <div className="flex items-start gap-2 px-2 pt-2">
          <Shield size={12} className="text-[#8A8A93] mt-0.5 flex-shrink-0" />
          <p className="text-[10.5px] text-[#8A8A93] leading-snug">
            Rewards only unlock once your friend passes verification and becomes active. This keeps the community real and blocks fake-account farming.
          </p>
        </div>
      </div>
    </div>
  );
}
